import React from 'react';
import cx from 'classnames';
import { Error as ErrorIcon } from '../../Icons';

const HELPER_ICON_SIZE_MAP = {
  sm: 16,
  md: 18,
  lg: 20,
};

type FormControlContextType = {
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  size?: 'sm' | 'md' | 'lg';
};

const FormControlContext = React.createContext<FormControlContextType | null>(
  null
);

const useFormControlContext = () => {
  const context = React.useContext(FormControlContext);

  if (context == null) {
    throw new Error(
      'FormControl components must be wrapped in <FormControl />'
    );
  }

  return context;
};

/**
 * * Form Control Container
 */
export interface FormControlContainerProps
  extends React.HTMLAttributes<HTMLDivElement> {
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const FormControlContainer = ({
  className,
  required = false,
  disabled = false,
  error = false,
  size = 'sm',
  children,
  ...rest
}: FormControlContainerProps) => {
  return (
    <FormControlContext.Provider value={{ required, disabled, error, size }}>
      <div
        className={cx(
          'form-control',
          { required, disabled, error },
          size,
          className && className
        )}
        {...rest}
      >
        {children}
      </div>
    </FormControlContext.Provider>
  );
};

/**
 * * Form Control Label
 */
// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface FormControlLabelProps
  extends React.LabelHTMLAttributes<HTMLLabelElement> {}

const FormControlLabel: React.FC<FormControlLabelProps> = ({
  className,
  children,
  ...props
}) => {
  return (
    <>
      <label
        {...props}
        className={cx('form-control-label', className && className)}
      >
        {children}
      </label>
      <div className="h-3" />
    </>
  );
};

/**
 * * Form Control Helper Text
 */
// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface FormControlHelperTextProps
  extends React.HtmlHTMLAttributes<HTMLSpanElement> {}

const FormControlHelperText = ({
  className,
  children,
  ...rest
}: FormControlHelperTextProps) => {
  const { error, size } = useFormControlContext();

  return (
    <span
      className={cx(
        'form-control-helper-text',
        error ? 'text-uta-error' : 'text-uta-grey',
        className && className
      )}
      {...rest}
    >
      {error && typeof children === 'string' && (
        <ErrorIcon
          className="fill-uta-error"
          width={HELPER_ICON_SIZE_MAP[size]}
          height={HELPER_ICON_SIZE_MAP[size]}
        />
      )}
      {children}
    </span>
  );
};

/**
 * * Compound Form Control Component
 */

interface CompoundedComponent
  extends React.ForwardRefExoticComponent<
    FormControlContainerProps &
      React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLDivElement>,
        HTMLDivElement
      >
  > {
  Label: typeof FormControlLabel;
  HelperText: typeof FormControlHelperText;
}

const FormControl = FormControlContainer as CompoundedComponent;
FormControl.Label = FormControlLabel;
FormControl.HelperText = FormControlHelperText;

export default FormControl;
